(() => {
  'use strict';
  const script = document.getElementById('MgPcAudioScript');
  const manifestUrl = script?.dataset.manifestUrl ? new URL(script.dataset.manifestUrl, document.baseURI).href : '';
  if (!manifestUrl) return;
  const mounted = new WeakSet();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let audioContext;
  let manifestPromise;

  function mount(root, entry) {
    if (mounted.has(root) || !entry?.file || root.dataset.mgConfigurator === 'true') return;
    const panel = root.querySelector('[data-mg-audio]');
    const media = panel?.querySelector('audio[data-mg-audio-player]');
    const wave = panel?.querySelector('.mg-rex-audio__wave');
    if (!media || !wave) return;
    mounted.add(root);
    const canvas = document.createElement('canvas');
    canvas.setAttribute('data-mg-audio-spectrum', '');
    canvas.setAttribute('aria-hidden', 'true');
    wave.appendChild(canvas);
    const context = canvas.getContext('2d');
    let analyser;
    let mediaSource;
    let bins;
    let frame = 0;
    let lastDraw = 0;
    let visible = true;
    let gradient;
    let width = 0;
    let height = 0;

    function stop() {
      if (frame) cancelAnimationFrame(frame);
      frame = 0;
      panel.dataset.mgSpectrum = 'idle';
    }
    function measure() {
      const bounds = wave.getBoundingClientRect();
      if (!bounds.width || !bounds.height || !context) return;
      width = bounds.width;
      height = bounds.height;
      const scale = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(width * scale);
      canvas.height = Math.round(height * scale);
      context.setTransform(scale, 0, 0, scale, 0, 0);
      gradient = context.createLinearGradient(0, 0, width, 0);
      [[0,'#FF2D78'],[.32,'#A855FF'],[.64,'#26CFFF'],[1,'#B9FF00']].forEach(([position, color]) => gradient.addColorStop(position, color));
    }
    function active() {
      return Boolean(analyser && context && !media.paused && !media.ended && visible && !document.hidden && !reducedMotion.matches && root.isConnected);
    }
    function tick(now) {
      frame = 0;
      if (!active()) { stop(); return; }
      // Cap at 30 redraws per second; no timer runs while paused or off screen.
      if (now - lastDraw >= 1000 / 30) {
        lastDraw = now;
        if (!width) measure();
        analyser.getByteFrequencyData(bins);
        context.clearRect(0, 0, width, height);
        context.fillStyle = gradient;
        const count = 72;
        const step = width / count;
        for (let i = 0; i < count; i += 1) {
          const bin = Math.min(bins.length - 1, Math.round(Math.pow(i / (count - 1), 1.7) * (bins.length - 1)));
          const amplitude = Math.max(2, (bins[bin] / 255) * height * .9);
          context.fillRect(i * step, (height - amplitude) / 2, Math.max(1, step * .45), amplitude);
        }
        panel.dataset.mgSpectrum = 'live';
      }
      frame = requestAnimationFrame(tick);
    }
    function start() {
      if (!active() || frame) return;
      measure();
      frame = requestAnimationFrame(tick);
    }
    function prepareSpectrum(event) {
      if (!event.target.closest('[data-mg-audio-toggle]') || !(media.getAttribute('src') || media.dataset.mgAudioSource) || reducedMotion.matches) return;
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      if (!AudioContextClass || !context) return;
      try {
        if (!audioContext) audioContext = new AudioContextClass();
        if (!analyser) {
          mediaSource = audioContext.createMediaElementSource(media);
          analyser = audioContext.createAnalyser();
          analyser.fftSize = 512;
          analyser.smoothingTimeConstant = .72;
          bins = new Uint8Array(analyser.frequencyBinCount);
          mediaSource.connect(analyser);
          analyser.connect(audioContext.destination);
        }
        audioContext.resume().then(start).catch(() => {});
      } catch (_) { /* Playback remains available if a spectrum is unsupported. */ }
    }
    // Capture on the root, before the existing panel handler stops propagation.
    root.addEventListener('click', prepareSpectrum, true);
    media.addEventListener('playing', start);
    ['pause','ended','waiting','stalled','error'].forEach((event) => media.addEventListener(event, stop));
    const onVisibility = () => document.hidden ? stop() : start();
    const onMotion = () => reducedMotion.matches ? stop() : start();
    document.addEventListener('visibilitychange', onVisibility);
    reducedMotion.addEventListener('change', onMotion);
    const visibility = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      visible ? start() : stop();
    });
    visibility.observe(panel);
    const size = new ResizeObserver(measure);
    size.observe(wave);
    root.addEventListener('mg:audio-dispose', () => {
      stop();
      media.pause();
      visibility.disconnect();
      size.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
      reducedMotion.removeEventListener('change', onMotion);
      root.removeEventListener('click', prepareSpectrum, true);
      mediaSource?.disconnect();
      analyser?.disconnect();
    }, { once: true });
    if (entry.transcript) {
      const details = document.createElement('details');
      details.className = 'mg-pc-audio-transcript';
      const summary = document.createElement('summary');
      summary.textContent = 'LEGGI LA PRESENTAZIONE';
      const text = document.createElement('p');
      text.textContent = entry.transcript;
      details.append(summary, text);
      panel.appendChild(details);
    }
    const url = new URL(entry.file, manifestUrl);
    if (entry.md5) url.searchParams.set('v', entry.md5);
    root.dispatchEvent(new CustomEvent('mg:audio-source', { detail: { handle:root.dataset.mgProductHandle, url:url.href, duration:entry.duration } }));
    measure();
  }
  function init(scope = document) {
    const roots = [...scope.querySelectorAll('[data-mg-rex-pdp][data-mg-product-handle]')];
    if (scope.matches?.('[data-mg-rex-pdp][data-mg-product-handle]')) roots.unshift(scope);
    if (!roots.length) return;
    if (!manifestPromise) manifestPromise = fetch(manifestUrl, { credentials:'omit' }).then((response) => response.ok ? response.json() : {}).catch(() => ({}));
    manifestPromise.then((manifest) => roots.forEach((root) => mount(root, manifest[root.dataset.mgProductHandle])));
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once:true });
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => event.target.querySelectorAll('[data-mg-rex-pdp]').forEach((root) => root.dispatchEvent(new Event('mg:audio-dispose'))));
})();
